import { useCallback, useEffect, useState } from 'react';
// parts/ is under orchestrator/, eqDsp.ts sits at hooks/ level
import { clampHpfHz, clampLpfHz, setEqQ } from '../../../eqDsp';
import type { EngineRefs } from '../refs';

export interface EqParams {
  eqEnabled: boolean; setEqEnabled: (v: boolean) => void;
  eqHpfEnabled: boolean; setEqHpfEnabled: (v: boolean) => void;
  eqLpfEnabled: boolean; setEqLpfEnabled: (v: boolean) => void;
  eqQ: number; setQ: (q: number) => void;
  masterLowpassHz: number; setMasterLowpassHz: (hz: number) => void;
  masterHighpassHz: number; setMasterHighpassHz: (hz: number) => void;
}

export function useEqParams(refs: EngineRefs) {
  const [eqEnabled, setEqEnabled] = useState(false);
  const [eqHpfEnabled, setEqHpfEnabled] = useState(true);
  const [eqLpfEnabled, setEqLpfEnabled] = useState(true);
  const [eqQ, setEqQState] = useState(0.707);
  const [masterLowpassHz, setMasterLowpassHzState] = useState(20000);
  const [masterHighpassHz, setMasterHighpassHzState] = useState(20);

  const setMasterLowpassHz = useCallback((hz: number) => {
    const ctx = refs.audioCtxRef.current;
    const clamped = ctx ? clampLpfHz(hz, ctx) : Math.max(20, Math.min(24000, hz));
    setMasterLowpassHzState(clamped);
  }, [refs]);

  const setMasterHighpassHz = useCallback((hz: number) => {
    const ctx = refs.audioCtxRef.current;
    const clamped = ctx ? clampHpfHz(hz, ctx) : Math.max(20, Math.min(24000, hz));
    setMasterHighpassHzState(clamped);
  }, [refs]);

  const setQ = useCallback((q: number) => { setEqQState(Math.max(0.1, Math.min(10, q))); }, []);

  // Apply Q updates when EQ is enabled
  useEffect(() => {
    if (!eqEnabled) return;
    const hp = refs.masterHighpassRef.current;
    const lp = refs.masterFilterRef.current;
    if (hp && lp) { try { setEqQ({ hp, lp }, eqQ); } catch {} }
  }, [eqQ, eqEnabled]);

  return {
    eqEnabled, setEqEnabled,
    eqHpfEnabled, setEqHpfEnabled,
    eqLpfEnabled, setEqLpfEnabled,
    eqQ, setQ,
    masterLowpassHz, setMasterLowpassHz,
    masterHighpassHz, setMasterHighpassHz,
  } satisfies EqParams;
}
